import {
  CalendarIcon,
  ProviderConsumer as FluentUIThemeConsumer,
  SpeakerPersonIcon,
  pxToRem,
  Text,
  Tooltip
} from '@fluentui/react-northstar';
import { DateTime } from 'luxon';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AgentTimeIcon from '../../../../svg/agent-time-filled.svg';
import AssigneeIcon from '../../../../svg/assignee.svg';
import EmailIcon from '../../../../svg/email.svg';
import GroupIcon from '../../../../svg/group.svg';
import PriorityIcon from '../../../../svg/priority-icon.svg';
import TicketSVG from '../../../../svg/ticket.svg';
import { appState } from '../../../AppState';
import { threedigitized } from '../../../tikit/ticketHelper';
import { ConvertDateTimeToLocale, ShortDateLabels } from '../../common/ConvertDate';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { LICENSE_FEATURE, TICKET_DETAIL } from '../../utils/constants';
import { toTitleCase } from '../../utils/helper';
import { getCachedFeature } from '../../cache/FeatureCache';
import { CheckFeature } from '../CheckFeature';
import { SlaPills } from '../SlaPills/SlaPills';
import { TicketStatus } from '../TicketCardTemplate';
import { DurationText } from '../TicketTimeTracking/DurationText';
import { UserAvatar } from '../UserAvatar';
import { dropToFit } from './dropToFit';
import { showParentChild } from './TicketDetailInfo';
import Styles from './TicketDetailBar.module.css';

interface Props {
  ticketData: any;
  isTimeTrackingEnabled?: boolean;
  agentTimeUpdate?: { totalMinutes: number; modifiedDate?: string };
}

/**
 * Below this the title stops giving way and fields start dropping instead. A title shorter
 * than this keeps its own width; the rule is only that a long one is never squeezed further.
 * 260 rather than 160 (Shaun, 2026-10-05): at 1920 with both side panes open, 160 let group
 * and agent time crowd a long title down to 253px; 260 gives it 306px for agent time alone.
 */
const TITLE_MIN_WIDTH = 260;

type FieldKey = 'requester' | 'priority' | 'assignee' | 'group' | 'agentTime' | 'created' | 'createdTime';

/**
 * Which fields give way first when the bar runs out of room, highest number first. The
 * requester is the last to go because it is who the ticket is for; the created date first
 * because it is the least likely reason to glance at a collapsed ticket - and before that,
 * just its time, which leaves the date standing in its place.
 */
const DROP_ORDER: Record<FieldKey, number> = {
  requester: 1,
  priority: 2,
  assignee: 3,
  group: 4,
  agentTime: 5,
  created: 6,
  createdTime: 7
};

/** Where each field sits on the bar, left to right - the order the title's tooltip lists them. */
const TOOLTIP_ORDER: FieldKey[] = ['requester', 'priority', 'assignee', 'group', 'agentTime', 'created'];

const labelStyle: React.CSSProperties = { color: '#8f90a6' };

/**
 * How long the row stays invisible waiting for its licence. It normally comes from memory in a
 * few milliseconds; if the feature cache is still loading, the row shows anyway and the SLA
 * pill falls back to CheckFeature, arriving late and refitting the row as it does.
 */
const LICENCE_WAIT_MS = 300;

type BarLicences = { sla: boolean };

/**
 * The SLA pill is licensed. Through CheckFeature it appears a render after everything else, so
 * the row was fitted without it, painted, and refitted - the title and the fields visibly
 * jumped on load. Resolving the licence first lets the first fit be the real one.
 */
const useBarLicences = () => {
  const [licences, setLicences] = useState<BarLicences | null>(null);
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => live && setWaited(true), LICENCE_WAIT_MS);
    getCachedFeature(LICENSE_FEATURE.ServiceLevelAgreements).then(sla => {
      if (live) setLicences({ sla });
    }).catch(() => live && setWaited(true));
    return () => { live = false; clearTimeout(timer); };
  }, []);
  return { licences, ready: licences != null || waited };
};

/**
 * The ticket card folded down to one row: what the ticket is and where it stands, without
 * labels. Fields that do not fit are hidden by DROP_ORDER - measured, not predicted from
 * breakpoints, because every value here is a tenant's own string - and listed in the
 * title's tooltip, so they stay reachable without expanding. The card's counters are left
 * out: the room goes to the title, and the counts are beside the bar already (the activity,
 * the Attachments section) or one click away in the expanded card.
 */
export const TicketDetailBar = ({
  ticketData,
  isTimeTrackingEnabled = false,
  agentTimeUpdate
}: Props) => {
  const { t } = useTranslation();
  const { priority } = appState();
  const barRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [hidden, setHidden] = useState<FieldKey[]>([]);
  const { licences, ready } = useBarLicences();
  // The fields the bar has dropped, listed in the title's tooltip in the order they sit on the
  // bar, left to right. A dropped time alone is not listed: the date still stands in for it.
  const hiddenInTooltip = TOOLTIP_ORDER.filter(key => hidden.includes(key));

  const none = t('ticket-details.ticket-details-card.ticket-details-info.none');
  const priorityText = showParentChild(ticketData?.Priority, priority).join(' > ') || none;
  const assigneeText = ticketData?.Assignee?.FullName ?? none;
  const groupText = ticketData?.SupportGroup?.Name ?? none;
  const teamText = ticketData?.Team?.FriendlyName ?? none;
  const requesterText = ticketData?.Requester?.FullName ?? none;
  const isEmail = ticketData?.Source?.Value == 'Email';
  const createdFull = [
    t('ticket-details.ticket-details-card.ticket-details-info.created'),
    ConvertDateTimeToLocale(ticketData?.CreatedDate, DateTime.DATE_MED_WITH_WEEKDAY),
    ...(isEmail ? [t('ticket-details.ticket-details-card.ticket-details-info.via-email')] : [])
  ].join(' ');
  const createdLabels = ShortDateLabels(ticketData?.CreatedDate);
  const totalAgentTimeMinutes = agentTimeUpdate?.totalMinutes ?? ticketData?.TotalTimeSpentMinutes;
  const ticketSlas: TicketSla[] = ticketData?.TicketSlas || [];

  // Runs before paint and on every resize, so a field is never seen and then removed.
  // The DOM is changed directly rather than through state: deciding what fits needs a
  // measure after each hide, and a render per step would flash.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const title = titleRef.current;
    if (!bar || !title) return undefined;

    // A field may have a stand-in shown while it is hidden: the created date alone, for the
    // date-and-time.
    const setStandIn = (field: HTMLElement, display: string) => {
      const standIn = bar.querySelector<HTMLElement>(`[data-stand-in-for="${field.dataset.key}"]`);
      if (standIn) standIn.style.display = display;
    };

    const fit = () => {
      const dropped = dropToFit(
        Array.from(bar.querySelectorAll<HTMLElement>('[data-drop]')),
        f => Number(f.dataset.drop),
        f => { f.style.display = ''; setStandIn(f, 'none'); },
        f => { f.style.display = 'none'; setStandIn(f, ''); },
        () => bar.scrollWidth > bar.clientWidth || title.clientWidth < Math.min(TITLE_MIN_WIDTH, title.scrollWidth)
      ).map(f => f.dataset.key as FieldKey);
      setHidden(prev => (prev.join() === dropped.join() ? prev : dropped));
    };

    fit();
    // Items can widen without resizing the bar - the SLA pill appears only after its licence
    // check resolves. The title gives way first, so watching it catches any of them.
    const observer = new ResizeObserver(fit);
    observer.observe(bar);
    observer.observe(title);
    return () => observer.disconnect();
  }, [ticketData, priorityText, totalAgentTimeMinutes, isTimeTrackingEnabled, createdLabels.withTime, licences]);

  const hiddenLines: Record<FieldKey, string> = {
    requester: `${toTitleCase(TICKET_DETAIL.REQUESTER)}: ${requesterText}`,
    priority: `${toTitleCase(TICKET_DETAIL.PRIORITY)}: ${priorityText}`,
    assignee: `${toTitleCase(TICKET_DETAIL.ASSIGNEE)}: ${assigneeText}`,
    group: `${toTitleCase(TICKET_DETAIL.GROUP)}: ${groupText}`,
    agentTime: `${toTitleCase(TICKET_DETAIL.TOTAL_AGENT_TIME)}: `,
    // Shorter than the created field's own tooltip, and in the "Label: value" form of the
    // lines around it: the bar's own date, no weekday.
    created: [
      `${t('ticket-details.ticket-details-card.ticket-details-info.created')}:`,
      createdLabels.withTime,
      ...(isEmail ? [t('ticket-details.ticket-details-card.ticket-details-info.via-email')] : [])
    ].join(' '),
    createdTime: ''
  };

  // One line of the title's tooltip for a field the bar has hidden.
  const hiddenLine = (key: FieldKey) => {
    if (key === 'agentTime')
      return <div key={key}>{hiddenLines.agentTime}<DurationText minutes={totalAgentTimeMinutes} plain /></div>;
    if (key === 'group')
      // The team's only home on the bar is the group field's tooltip, so it comes along
      // when the group is hidden.
      return (
        <React.Fragment key={key}>
          <div>{hiddenLines.group}</div>
          <CheckFeature featureName={LICENSE_FEATURE.MultiDepartment}>
            <div>{toTitleCase(TICKET_DETAIL.TEAM)}: {teamText}</div>
          </CheckFeature>
        </React.Fragment>
      );
    return <div key={key}>{hiddenLines[key]}</div>;
  };

  const slaPill = (
    <span className={`${Styles.field} ${Styles.slaField}`}>
      <SlaPills ticketSlas={ticketSlas} svgGap={false} viewType="List" />
    </span>
  );

  const field = (key: FieldKey, tip: React.ReactNode, children: React.ReactNode) => (
    <Tooltip
      align="center"
      position="below"
      content={{ content: tip, styles: { maxWidth: 'none', whiteSpace: 'nowrap' } }}
      trigger={
        <span className={Styles.field} data-drop={DROP_ORDER[key]} data-key={key}>
          {children}
        </span>
      }
    />
  );

  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <div
          ref={barRef}
          className={Styles.bar}
          style={{
            color: 'var(--mgt-theme-default-foreground)',
            backgroundColor: 'var(--mgt-theme-background)',
            ...ThemeColorScheme(globalTheme.siteVariables)
          }}
          // The box paints at once, so nothing below it moves; only its contents wait.
          data-waiting={ready ? undefined : ''}
        >
          <span className={Styles.field}>
            <TicketSVG width={24} />
            <Text>#{threedigitized(ticketData?.Id)}</Text>
          </span>

          {field('requester', hiddenLines.requester,
            ticketData?.Requester != null ? (
              <UserAvatar
                idOrUpn={ticketData.Requester?.AadObjectId ?? ticketData.Requester?.UserName}
                avatarProps={{ name: ticketData.Requester?.FullName, size: 'small' }}
              />
            ) : (
              <SpeakerPersonIcon size="small" style={labelStyle} variables={{ largeSize: '30px' }} />
            )
          )}

          <Tooltip
            align="start"
            position="below"
            content={{
              // The title wraps at Northstar's usual tooltip width (246px less its 12px
              // padding each side); the field lines never wrap, and the tooltip grows to fit
              // one that is longer - a long date in some languages.
              styles: { maxWidth: 'none' },
              content: (
                <div>
                  <div style={{ maxWidth: `calc(${pxToRem(246)} - ${pxToRem(24)})` }}>{ticketData?.Title}</div>
                  {hiddenInTooltip.length > 0 && (
                    <div className="mt-2 whitespace-nowrap">
                      {hiddenInTooltip.map(hiddenLine)}
                    </div>
                  )}
                </div>
              )
            }}
            trigger={
              <span ref={titleRef} className={Styles.title}>{ticketData?.Title}</span>
            }
          />

          {/* status-pill-cell sizes the pill to match the SLA pill beside it, as the ticket
              list does (main.css). */}
          <span className={`${Styles.field} ${Styles.statusField} status-pill-cell`}>
            <TicketStatus status={ticketData?.Status} />
          </span>

          {/* With the status rather than among the fields that give way: whether the ticket is
              on time is as much "where it stands" as its status. The list's form - one pill, the
              rest behind "+N", no label - matches how the ticket list shows it. */}
          {ticketSlas.length > 0 && (licences ? licences.sla && slaPill : (
            <CheckFeature featureName={LICENSE_FEATURE.ServiceLevelAgreements}>{slaPill}</CheckFeature>
          ))}

          {field('priority', hiddenLines.priority, <>
            <PriorityIcon width={20} height={20} className={`${Styles.icon} ${Styles.dot}`} />
            <span className={Styles.value}>{priorityText}</span>
          </>)}

          {field('assignee', hiddenLines.assignee, <>
            <AssigneeIcon width={12} height={13} className={Styles.icon} />
            <span className={Styles.value}>{assigneeText}</span>
          </>)}

          {field('group',
            <div>
              <div>{hiddenLines.group}</div>
              <CheckFeature featureName={LICENSE_FEATURE.MultiDepartment}>
                <div>{toTitleCase(TICKET_DETAIL.TEAM)}: {teamText}</div>
              </CheckFeature>
            </div>,
            <>
              <GroupIcon className={Styles.icon} />
              <span className={Styles.value}>{groupText}</span>
            </>
          )}

          {isTimeTrackingEnabled && field('agentTime',
            <span>{hiddenLines.agentTime}<DurationText minutes={totalAgentTimeMinutes} plain /></span>,
            <>
              <AgentTimeIcon width={15} height={15} className={Styles.icon} />
              <span className={Styles.value}><DurationText minutes={totalAgentTimeMinutes} plain /></span>
            </>
          )}

          {field('created', createdFull, <>
            <CalendarIcon size="small" style={labelStyle} className={Styles.icon} />
            <span className={`${Styles.value} ${Styles.date}`} data-drop={DROP_ORDER.createdTime} data-key="createdTime">{createdLabels.withTime}</span>
            <span className={`${Styles.value} ${Styles.date}`} data-stand-in-for="createdTime" style={{ display: 'none' }}>{createdLabels.dateOnly}</span>
            {isEmail && <EmailIcon width={12} height={12} stroke="#8f90a6" className={Styles.icon} />}
          </>)}
        </div>
      )}
    />
  );
};
